import type { Booking } from "../../types/booking";
import type { CommercialInvoiceAnalysisResult } from "./types";
import { crossCheckCommercialInvoiceWithBooking } from "./bookingCrossCheck";
import {
  getCommercialInvoiceValidityBreakdown,
  type CommercialInvoiceValidityBreakdown,
} from "./commercialInvoiceValidity";
import { formatCiDisplayValue } from "./formatCiDisplayValue";

export type CiIssueSeverity = "error" | "warning";

export interface CiAnalysisIssue {
  id: string;
  severity: CiIssueSeverity;
  title: string;
  detail?: string;
}

const AI_FIELD_LABELS: Record<string, string> = {
  sellerName: "Seller name",
  sellerAddress: "Seller address",
  buyerName: "Buyer name",
  buyerAddress: "Buyer address",
  buyerVat: "Buyer VAT",
  invoiceNumber: "Invoice number",
  invoiceDate: "Invoice date",
  incoterm: "Incoterm",
  currency: "Currency",
  totalAmount: "Total amount",
  grossWeight: "Gross weight",
  netWeight: "Net weight",
  countryOfOrigin: "Country of origin",
};

const comparisonIssueTitle = (
  field: string,
  status: string
): { title: string; detail?: string } => {
  const label = field.trim() || "Field";
  switch (status) {
    case "mismatch":
      return { title: `${label} does not match booking` };
    case "missing_invoice":
      return { title: `${label} not found on invoice` };
    case "missing_booking":
      return { title: `${label} missing on booking` };
    default:
      return { title: `${label} needs review` };
  }
};

export function getCommercialInvoiceAnalysisIssues(
  result: CommercialInvoiceAnalysisResult,
  booking?: Booking | null,
  breakdown?: CommercialInvoiceValidityBreakdown
): CiAnalysisIssue[] {
  const validity =
    breakdown ?? getCommercialInvoiceValidityBreakdown(result, booking);
  const issues: CiAnalysisIssue[] = [];
  const seen = new Set<string>();

  const push = (issue: CiAnalysisIssue) => {
    const key = `${issue.severity}:${issue.title}`;
    if (seen.has(key)) return;
    seen.add(key);
    issues.push(issue);
  };

  if (!validity.isNonProforma) {
    push({
      id: "proforma",
      severity: "error",
      title: "Document may be a proforma / PI — not valid for customs",
      detail: validity.nonProformaDetail || undefined,
    });
  }

  if (validity.completenessScore < 70 && !validity.hasDeepCheck) {
    push({
      id: "completeness",
      severity: "warning",
      title: "Invoice is missing required information",
      detail: `${validity.completenessDetail} (${validity.completenessScore}%)`,
    });
  }

  if (
    validity.matchScore != null &&
    validity.matchScore < 70 &&
    !validity.hasDeepCheck
  ) {
    push({
      id: "match",
      severity: "warning",
      title: "Invoice data does not fully match the booking",
      detail: `${validity.matchDetail} (${validity.matchScore}%)`,
    });
  }

  const deepCheck = result.deepCheck;
  if (deepCheck?.available) {
    for (const row of deepCheck.bookingComparisons ?? []) {
      if (row.status === "match") continue;
      const { title, detail } = comparisonIssueTitle(row.field, row.status);
      const parts: string[] = [];
      const bookingText = formatCiDisplayValue(row.bookingValue);
      const invoiceText = formatCiDisplayValue(row.invoiceValue);
      if (bookingText) parts.push(`Booking: ${bookingText}`);
      if (invoiceText) parts.push(`Invoice: ${invoiceText}`);
      push({
        id: `ai-${row.field}-${row.status}`,
        severity: row.status === "mismatch" ? "error" : "warning",
        title,
        detail: detail ?? (parts.length ? parts.join(" · ") : undefined),
      });
    }

    if (deepCheck.extracted) {
      for (const [key, label] of Object.entries(AI_FIELD_LABELS)) {
        const value = deepCheck.extracted[key as keyof typeof deepCheck.extracted];
        if (value != null && String(value).trim() !== "") continue;
        push({
          id: `ai-missing-${key}`,
          severity: "warning",
          title: `${label} not extracted from invoice`,
        });
      }
    }
  } else if (booking) {
    const crossCheck = crossCheckCommercialInvoiceWithBooking(result, booking);
    for (const row of crossCheck.rows) {
      if (row.status === "match" || row.status === "skipped") continue;
      push({
        id: `auto-${row.id}`,
        severity: row.status === "mismatch" ? "error" : "warning",
        title:
          row.status === "missing_invoice"
            ? `${row.label} not found on invoice`
            : row.status === "mismatch"
              ? `${row.label} does not match booking`
              : `${row.label} needs review`,
        detail: row.bookingValue ? `Booking: ${row.bookingValue}` : undefined,
      });
    }
  }

  if (!validity.hasDeepCheck) {
    for (const field of result.requiredFields) {
      if (field.present) continue;
      push({
        id: `field-${field.id}`,
        severity: "warning",
        title: `${field.label} missing on invoice`,
      });
    }
  }

  if (!validity.hasDeepCheck) {
    for (const warning of result.warnings) {
      push({
        id: `warn-${warning.slice(0, 40)}`,
        severity: "warning",
        title: warning,
      });
    }
  }

  const severityOrder: Record<CiIssueSeverity, number> = { error: 0, warning: 1 };
  return issues.sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity]
  );
}

export function getOverallCiVerdict(
  issues: CiAnalysisIssue[],
  validity: CommercialInvoiceValidityBreakdown
): { tone: "success" | "warning" | "error"; label: string } {
  if (issues.some((i) => i.severity === "error") || !validity.isNonProforma) {
    return { tone: "error", label: "Needs attention before customs" };
  }
  if (issues.length > 0 || validity.completenessScore < 85 || (validity.matchScore ?? 100) < 85) {
    return { tone: "warning", label: "Review recommended" };
  }
  return { tone: "success", label: "Looks good" };
}
